/**
 * ModuleAccessPanel — zarządzanie dostępem do modułu per szablon roli.
 * Wyświetla macierz: rola × (widok / edycja / usuwanie rekordów).
 * Używany w zakładce "Dostęp" ustawień modułu.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield, ShieldAlert } from 'lucide-react';

import { api } from '@/lib/api';

interface RoleAccess {
  templateId: string;
  templateName: string;
  isSystem: boolean;
  view: boolean;
  edit: boolean;
  delete: boolean;
}

interface AccessResponse {
  roles: RoleAccess[];
}

interface ModuleAccessPanelProps {
  moduleId: string;
  moduleSlug: string;
}

export function ModuleAccessPanel({ moduleId, moduleSlug }: ModuleAccessPanelProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const query = useQuery<AccessResponse>({
    queryKey: ['module-access', moduleId],
    queryFn: () => api.get<AccessResponse>(`/modules/${moduleId}/access`).then((r) => r.data),
    enabled: !!moduleId,
  });

  // Stan lokalny — kopia do edycji (zainicjowany z danych serwera)
  const [localRoles, setLocalRoles] = useState<RoleAccess[] | null>(null);
  const roles = localRoles ?? query.data?.roles ?? [];

  // Gdy dane przyjdą po raz pierwszy, zainicjuj lokalny stan
  if (query.data && localRoles === null) {
    setLocalRoles(query.data.roles);
  }

  const mutation = useMutation({
    mutationFn: (roles: RoleAccess[]) =>
      api
        .put(`/modules/${moduleId}/access`, {
          roles: roles.map((r) => ({
            templateId: r.templateId,
            view: r.view,
            edit: r.edit,
            delete: r.delete,
          })),
        })
        .then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['module-access', moduleId] });
    },
  });

  function toggle(templateId: string, perm: 'view' | 'edit' | 'delete') {
    setLocalRoles((prev) =>
      (prev ?? []).map((r) => {
        if (r.templateId !== templateId) return r;
        const next = { ...r, [perm]: !r[perm] };
        // edit/delete implikuje view
        if ((perm === 'edit' || perm === 'delete') && next[perm]) {
          next.view = true;
        }
        // gdy view wyłączone — wyłącz też edit i delete
        if (perm === 'view' && !next.view) {
          next.edit = false;
          next.delete = false;
        }
        return next;
      }),
    );
  }

  function handleSave() {
    if (localRoles) {
      mutation.mutate(localRoles);
    }
  }

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        {t('common.loadError')}
      </div>
    );
  }

  if (roles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-sm text-muted-foreground">
        <Shield className="h-8 w-8 opacity-30" />
        <p>{t('tenant.moduleSettings.access.noRoles')}</p>
      </div>
    );
  }

  const isDirty = JSON.stringify(localRoles) !== JSON.stringify(query.data?.roles ?? []);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('tenant.moduleSettings.access.description')}
      </p>

      <div className="overflow-hidden rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="px-4 py-3 text-left font-medium">
                {t('tenant.moduleSettings.access.role')}
              </th>
              <th className="px-4 py-3 text-center font-medium">
                {t('tenant.moduleSettings.access.view')}
              </th>
              <th className="px-4 py-3 text-center font-medium">
                {t('tenant.moduleSettings.access.edit')}
              </th>
              <th className="px-4 py-3 text-center font-medium">
                {t('tenant.moduleSettings.access.delete')}
              </th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role, idx) => (
              <tr key={role.templateId} className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{role.templateName}</span>
                    {role.isSystem && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        {t('tenant.moduleSettings.access.system')}
                      </span>
                    )}
                  </div>
                </td>
                {(['view', 'edit', 'delete'] as const).map((perm) => (
                  <td key={perm} className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={role[perm]}
                      onChange={() => toggle(role.templateId, perm)}
                      className="h-4 w-4 cursor-pointer rounded border-input accent-primary"
                      aria-label={`${role.templateName} ${perm}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {t('tenant.moduleSettings.access.hint', { slug: moduleSlug })}
        </p>
        <button
          onClick={handleSave}
          disabled={!isDirty || mutation.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending ? t('common.saving') : t('tenant.moduleSettings.access.save')}
        </button>
      </div>

      {mutation.isSuccess && !isDirty && (
        <p className="text-xs text-green-600">{t('tenant.moduleSettings.access.saved')}</p>
      )}
      {mutation.isError && <p className="text-xs text-destructive">{t('common.saveError')}</p>}
    </div>
  );
}
